import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import {
  useTimeLogs,
  useCreateTimeLog,
  useUpdateTimeLog,
  useDeleteTimeLog,
} from '../hooks/useTimeLogs';
import { useTodos } from '../hooks/useTodos';
import { useCategories } from '../hooks/useCategories';
import { TimeLogEntry, CreateTimeLogEntry } from '../types/api';
import { useToast } from '../components/ToastProvider';
import ConfirmDialog from '../components/ConfirmDialog';
import EmptyState from '../components/EmptyState';
import { getApiErrorMessage } from '../utils/errors';
import {
  formatDate,
  formatDuration,
  formatDurationString,
  parseDurationToSeconds,
  secondsToHoursMinutes,
  toDurationPayload,
  todayISODate,
} from '../utils/datetime';

interface LogFormState {
  taskName: string;
  description: string;
  entryDate: string; // YYYY-MM-DD
  hours: string;
  minutes: string;
  todo: number | '';
  category: number | '';
}

const emptyForm: LogFormState = {
  taskName: '',
  description: '',
  entryDate: todayISODate(),
  hours: '0',
  minutes: '30',
  todo: '',
  category: '',
};

const TimeLogsPage: React.FC = () => {
  const { data: logs, isLoading, isError, error, refetch } = useTimeLogs();
  const { data: todos } = useTodos();
  const { data: categories } = useCategories();
  const createLog = useCreateTimeLog();
  const updateLog = useUpdateTimeLog();
  const deleteLog = useDeleteTimeLog();
  const { showToast } = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<TimeLogEntry | null>(null);
  const [form, setForm] = useState<LogFormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [logToDelete, setLogToDelete] = useState<TimeLogEntry | null>(null);

  // Group entries by date, newest day first.
  const groups = useMemo(() => {
    const byDate = new Map<string, TimeLogEntry[]>();
    for (const log of logs ?? []) {
      const list = byDate.get(log.entry_date) ?? [];
      list.push(log);
      byDate.set(log.entry_date, list);
    }
    return Array.from(byDate.entries())
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([date, entries]) => ({
        date,
        entries: entries.sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
        totalSeconds: entries.reduce((sum, e) => sum + parseDurationToSeconds(e.time_logged), 0),
      }));
  }, [logs]);

  const weekTotal = useMemo(() => {
    const since = Date.now() - 7 * 86400000;
    return (logs ?? [])
      .filter((l) => new Date(`${l.entry_date}T00:00:00`).getTime() >= since)
      .reduce((sum, l) => sum + parseDurationToSeconds(l.time_logged), 0);
  }, [logs]);

  const openCreate = () => {
    setEditingLog(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (log: TimeLogEntry) => {
    const { hours, minutes } = secondsToHoursMinutes(parseDurationToSeconds(log.time_logged));
    setEditingLog(log);
    setForm({
      taskName: log.task_name,
      description: log.description ?? '',
      entryDate: log.entry_date,
      hours: String(hours),
      minutes: String(minutes),
      todo: log.belongs_to_todo ?? '',
      category: log.category ?? '',
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const hours = parseInt(form.hours || '0', 10);
    const minutes = parseInt(form.minutes || '0', 10);
    if (hours * 60 + minutes <= 0) {
      setFormError('Duration must be greater than zero.');
      return;
    }

    const payload: CreateTimeLogEntry = {
      task_name: form.taskName.trim(),
      description: form.description.trim(),
      entry_date: form.entryDate,
      time_logged: toDurationPayload(hours, minutes),
      belongs_to_todo: form.todo === '' ? null : form.todo,
      category: form.category === '' ? null : form.category,
    };

    const onError = (err: unknown) => setFormError(getApiErrorMessage(err));

    if (editingLog) {
      updateLog.mutate(
        { id: editingLog.id, data: payload },
        {
          onSuccess: () => {
            setDialogOpen(false);
            showToast('Time log updated');
          },
          onError,
        }
      );
    } else {
      createLog.mutate(payload, {
        onSuccess: () => {
          setDialogOpen(false);
          showToast('Time logged');
        },
        onError,
      });
    }
  };

  const confirmDelete = () => {
    if (!logToDelete) return;
    deleteLog.mutate(logToDelete.id, {
      onSuccess: () => {
        setLogToDelete(null);
        showToast('Time log deleted');
      },
      onError: (err) => {
        setLogToDelete(null);
        showToast(getApiErrorMessage(err), 'error');
      },
    });
  };

  const saving = createLog.isPending || updateLog.isPending;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1080, width: '100%', mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5">Time Logs</Typography>
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading…' : `${formatDuration(weekTotal)} logged in the last 7 days`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
          Log time
        </Button>
      </Stack>

      {isError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          {getApiErrorMessage(error, 'Could not load your time logs.')}
        </Alert>
      ) : isLoading ? (
        <Stack spacing={1.5}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={86} sx={{ borderRadius: 3.5 }} />
          ))}
        </Stack>
      ) : groups.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ScheduleRoundedIcon />}
            title="No time logged yet"
            description="Record how long you spend on tasks to see where your time goes."
            actionLabel="Log your first entry"
            onAction={openCreate}
          />
        </Card>
      ) : (
        <Stack spacing={3}>
          {groups.map((group) => (
            <Box key={group.date}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1, px: 0.5 }}>
                <Typography variant="subtitle1">{formatDate(group.date)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total: {formatDuration(group.totalSeconds)}
                </Typography>
              </Stack>
              <Card>
                {group.entries.map((log, idx) => (
                  <React.Fragment key={log.id}>
                    {idx > 0 && <Divider />}
                    <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Chip
                        label={formatDurationString(log.time_logged)}
                        color="primary"
                        sx={{ fontVariantNumeric: 'tabular-nums', mt: 0.25, minWidth: 72 }}
                      />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                          {log.task_name}
                        </Typography>
                        {log.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {log.description}
                          </Typography>
                        )}
                        {(log.belongs_to_todo_title || log.category_name) && (
                          <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}>
                            {log.belongs_to_todo_title && (
                              <Chip
                                size="small"
                                icon={<LinkRoundedIcon />}
                                label={log.belongs_to_todo_title}
                                variant="outlined"
                              />
                            )}
                            {log.category_name && (
                              <Chip
                                size="small"
                                icon={<FolderRoundedIcon />}
                                label={log.category_name}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        )}
                      </Box>
                      <Stack direction="row">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(log)} aria-label={`Edit ${log.task_name}`}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setLogToDelete(log)}
                            aria-label={`Delete ${log.task_name}`}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </React.Fragment>
                ))}
              </Card>
            </Box>
          ))}
        </Stack>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingLog ? 'Edit time log' : 'Log time'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.25} sx={{ mt: 0.5 }}>
              {formError && (
                <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{formError}</Alert>
              )}
              <TextField
                label="What did you work on?"
                value={form.taskName}
                onChange={(e) => setForm((f) => ({ ...f, taskName: e.target.value }))}
                required
                fullWidth
                autoFocus
                inputProps={{ maxLength: 200 }}
              />
              <TextField
                label="Notes"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Date"
                  type="date"
                  value={form.entryDate}
                  onChange={(e) => setForm((f) => ({ ...f, entryDate: e.target.value }))}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Hours"
                  type="number"
                  value={form.hours}
                  onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                  fullWidth
                  inputProps={{ min: 0, max: 99 }}
                />
                <TextField
                  label="Minutes"
                  type="number"
                  value={form.minutes}
                  onChange={(e) => setForm((f) => ({ ...f, minutes: e.target.value }))}
                  fullWidth
                  inputProps={{ min: 0, max: 59 }}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Linked task"
                  value={form.todo}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, todo: e.target.value === '' ? '' : Number(e.target.value) }))
                  }
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {(todos ?? []).map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Category"
                  value={form.category}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, category: e.target.value === '' ? '' : Number(e.target.value) }))
                  }
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {(categories ?? []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !form.taskName.trim() || !form.entryDate}
            >
              {saving ? 'Saving…' : editingLog ? 'Save changes' : 'Log time'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!logToDelete}
        title="Delete time log?"
        message={<>The entry “{logToDelete?.task_name}” will be permanently deleted.</>}
        loading={deleteLog.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setLogToDelete(null)}
      />
    </Box>
  );
};

export default TimeLogsPage;
